import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Player } from 'types/global'
import { useCountries } from 'hooks/use-countries'
import { getUnknownDisplayText, initializeHeightValues } from 'utils'
import ProfilePanel, { DataListItem } from './generic/profile-panel/ProfilePanel'

import { getDataListValueFields } from './generic/profile-panel/utils'

const getProfileHeightText = (height: number | undefined) => {
  if (!height || typeof height !== 'number') return undefined

  const { m, cm, ft, inches } = initializeHeightValues(height)

  return `${ft}' ${inches}" (${m === '0' ? '' : m}${cm.length < 2 ? '0' + cm : cm}cm)`
}

interface AdditionalInfoProps {
  profile: Player
}

const AdditionalInfo: React.FC<AdditionalInfoProps> = ({ profile }) => {
  const { t } = useTranslation(['strProfileHeader', 'strCommonUI'])
  const { getCountryName } = useCountries()

  const additionalInfo: DataListItem[] = useMemo(() => {
    const fallbackValue = '-'
    const fallbackAriaLabel = t('ariaLabel.noData', { ns: 'strCommonUI' })

    return [
      {
        label: t('residence', { ns: 'strProfileHeader' }),
        ...getDataListValueFields(profile.residence && getCountryName(profile.residence), {
          fallbackValue,
          fallbackAriaLabel,
        }),
      },
      {
        label: t('height', { ns: 'strProfileHeader' }),
        ...getDataListValueFields(getProfileHeightText(profile.height), {
          fallbackValue,
          fallbackAriaLabel,
        }),
      },
      {
        label: t('preferredHand', { ns: 'strProfileHeader' }),
        ...getDataListValueFields(
          profile.preferredHand && profile.preferredHand !== getUnknownDisplayText()
            ? profile.preferredHand
            : undefined,
          { fallbackValue, fallbackAriaLabel }
        ),
      },
      {
        label: t('backhand', { ns: 'strProfileHeader' }),
        ...getDataListValueFields(
          profile.backhand && profile.backhand !== getUnknownDisplayText()
            ? profile.backhand
            : undefined,
          { fallbackValue, fallbackAriaLabel }
        ),
      },
      {
        label: t('favouriteShot', { ns: 'strProfileHeader' }),
        ...getDataListValueFields(profile.favouriteShot, { fallbackValue, fallbackAriaLabel }),
      },
    ]
  }, [t, getCountryName, profile])

  return (
    <ProfilePanel title={t('additionalInfo', { ns: 'strProfileHeader' })}>
      <ProfilePanel.DataList items={additionalInfo} />
    </ProfilePanel>
  )
}

export default AdditionalInfo
